<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
const props = defineProps({
  width: {
    type: String,
    default: "",
  },
  minHeight: {
    type: String,
    default: "",
  },
  bodyVariant: {
    type: String,
    default: "plain",
  },
  showDefaultFooterClose: {
    type: Boolean,
    default: true,
  },
});
const store = useStore();
const emit = defineEmits(["hide"]);
const bootstrap = store.state.bootstrap;
const modalRef = ref(null);

onMounted(() => {
  const modalElement = modalRef.value;
  if (modalElement) {
    modalElement.addEventListener("hidden.bs.modal", onModalHide);
  }
});

onBeforeUnmount(() => {
  const modalElement = modalRef.value;
  if (modalElement) {
    modalElement.removeEventListener("hidden.bs.modal", onModalHide);
  }
});

function closeModal() {
  bootstrap.Modal.getInstance(modalRef.value)?.hide();
}
function onModalHide() {
  emit("hide");
}
const dialogStyle = computed(() => {
  const style = {};
  if (props.width !== "") {
    const responsiveWidth = `min(${props.width}, calc(100vw - 1rem))`;
    style.width = responsiveWidth;
    style.maxWidth = responsiveWidth;
  }
  return style;
});

const contentStyle = computed(() => {
  const style = {};
  if (props.minHeight !== "") {
    style.minHeight = props.minHeight;
  }
  return style;
});

defineExpose({
  closeModal,
});
</script>
<template>
  <div
    ref="modalRef"
    class="modal fade card-detail-layout premium-modal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog premium-modal-dialog mt-lg-10" :style="dialogStyle">
      <div class="modal-content premium-modal-content" :style="contentStyle">
        <div class="modal-header">
          <slot name="header">
            <h5 class="modal-title">Sin titulo</h5>
          </slot>
          <button
            type="button"
            class="btn-close text-xxs"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="closeModal"
          ></button>
        </div>
        <div
          class="modal-body"
          :class="{
            'modal-body--canvas': bodyVariant === 'canvas',
          }"
        >
          <slot name="body">
            <p>Sin contenido</p>
          </slot>
        </div>
        <div v-if="bodyVariant !== 'canvas'" class="modal-footer">
          <button
            v-if="showDefaultFooterClose"
            type="button"
            class="btn mb-0 btn-outline-primary btn-xs"
            data-bs-dismiss="modal"
            @click="closeModal()"
          >
            Cerrar
          </button>
          <slot name="footer">
          </slot>
        </div>
      </div>
    </div>
    <slot></slot>
  </div>
</template>

<style scoped>
.premium-modal {
  --premium-modal-surface: #ffffff;
  --premium-modal-border: rgba(15, 23, 42, 0.08);
  --premium-modal-border-strong: rgba(148, 163, 184, 0.24);
  --premium-modal-shadow:
    0 28px 70px rgba(15, 23, 42, 0.16),
    0 8px 24px rgba(15, 23, 42, 0.08);
  --premium-modal-radius: 18px;
}

.premium-modal :deep(.modal-backdrop.show) {
  opacity: 0.42;
}

.premium-modal-dialog {
  margin-top: clamp(1rem, 5vh, 3.5rem) !important;
}

.premium-modal-content {
  background: #ffffff;
  border: 1px solid var(--premium-modal-border);
  border-radius: var(--premium-modal-radius);
  box-shadow: var(--premium-modal-shadow);
  overflow: visible;
}

.modal-header {
  align-items: center;
  background: #ffffff;
  border-bottom: 1px solid var(--premium-modal-border-strong);
  padding: 0.8rem 0.95rem 0.7rem;
}

.modal-title {
  color: var(--color-text-primary);
  font-size: 0.96rem;
  font-weight: 600;
  letter-spacing: -0.015em;
  margin-bottom: 0;
  line-height: 1.1;
}

.modal-body {
  background: #ffffff;
  color: var(--color-text-primary);
  padding: 0.95rem;
}

.modal-body--canvas {
  background:
    radial-gradient(circle at top left, rgba(148, 163, 184, 0.07), transparent 34%),
    linear-gradient(180deg, #fbfcfe 0%, #f7f9fc 100%);
  border-radius: 0 0 calc(var(--premium-modal-radius) - 1px) calc(var(--premium-modal-radius) - 1px);
  padding: 0.9rem;
}

.btn-close {
  background-position: center;
  background-size: 0.65rem;
  border-radius: 999px;
  box-shadow: none;
  height: 1.1rem;
  margin: 0;
  opacity: 0.7;
  padding: 0.22rem;
  transition:
    background-color 0.18s ease,
    opacity 0.18s ease,
    transform 0.18s ease;
  width: 1.1rem;
}

.btn-close:hover,
.btn-close:focus {
  background-color: rgba(148, 163, 184, 0.14);
  opacity: 1;
  transform: scale(1.02);
}

.modal-footer {
  align-items: center;
  background: #ffffff;
  border-top: 1px solid var(--premium-modal-border-strong);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.75rem 0.95rem 0.9rem;
}

.modal-footer > * {
  margin: 0;
}

.modal-footer :deep(.btn),
.modal-footer :deep(button) {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  line-height: 1;
  margin: 0 !important;
  min-height: 1.8rem;
  vertical-align: middle;
}

.modal-footer :deep(.btn-xs) {
  padding-bottom: 0.42rem;
  padding-top: 0.42rem;
}

@media (max-width: 576px) {
  .premium-modal-dialog {
    margin: 0.75rem !important;
  }

  .premium-modal-content {
    border-radius: 16px;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding-left: 0.8rem;
    padding-right: 0.8rem;
  }
}
</style>
